import { useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Title,
  Text,
  Badge,
  Group,
  Stack,
  Paper,
  Button,
  Loader,
  Select,
  Divider,
  Grid,
  Alert,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconArrowLeft,
  IconDownload,
  IconAlertCircle,
} from "@tabler/icons-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ObservationAnalysisPanel,
  triggerAnalysis,
  isProcessingInProgress,
  type ObservationDetail as PackageObservationDetail,
  type ObservationProcessingStatus,
  type AnalysisCategory,
} from "@deep-consulting-solutions/observation-ui";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import ObservationCommentsThread from "../../../components/observations/ObservationCommentsThread";
import ObservationMetadataCard from "../../../components/observations/ObservationMetadataCard";
import { ObservationCategoryBadge } from "../../../components/observations/ObservationCategoryBadge";
import { useObservationDisplayCodes } from "../../../hooks/useObservationDisplayCodes";
import { observationsService } from "../../../services/observations.service";
import {
  OBSERVATION_SEVERITY_OPTIONS,
  OBSERVATION_STATUS_OPTIONS,
  isObservationSeverity,
  isObservationStatus,
  observationSeverityColor,
  observationStatusColor,
  observationStatusLabel,
  type ObservationSeverity,
  type ObservationStatus,
} from "../../../utils/observationStatus";
import styles from "./ObservationDetailPage.module.css";

dayjs.extend(utc);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

interface ObservationDetail {
  id: string;
  reporterId: string;
  reporterName: string;
  reporterEmail: string;
  recordingDownloadUrl: string | null;
  recordingStartedAt: string | null;
  sessionRecordingId: string | null;
  parentObservationId: string | null;
  childSegments: Array<{
    id: string;
    recordingDownloadUrl: string | null;
    recordingStartedAt: string | null;
  }>;
  status: ObservationStatus;
  severity: ObservationSeverity | null;
  processingStatus: ObservationProcessingStatus;
  transcript: string | null;
  title: string | null;
  summary: string | null;
  analysisMetadata: Record<string, unknown> | null;
  durationSeconds: number | null;
  processingError: string | null;
  processingCompletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const CATEGORY_OPTIONS: Array<{ value: AnalysisCategory; label: string }> = [
  { value: "bug", label: "Bug" },
  { value: "ux_friction", label: "UX Friction" },
  { value: "feature_request", label: "Feature Request" },
  { value: "question", label: "Question" },
  { value: "data_issue", label: "Data Issue" },
];

function readCategoryFromMetadata(
  metadata: Record<string, unknown> | null
): AnalysisCategory | null {
  const raw = metadata?.category;
  if (typeof raw !== "string") {
    return null;
  }
  const isKnown = CATEGORY_OPTIONS.some((opt) => opt.value === raw);
  return isKnown ? (raw as AnalysisCategory) : null;
}

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function useStableRecordingUrls(
  observationId: string | undefined,
  liveMainUrl: string | null | undefined,
  liveSegments: Array<{ id: string; recordingDownloadUrl: string | null }>
) {
  const cacheRef = useRef<{
    observationId: string | null;
    mainUrl: string | null;
    segmentUrls: Record<string, string | null>;
  }>({ observationId: null, mainUrl: null, segmentUrls: {} });

  if (observationId && cacheRef.current.observationId !== observationId) {
    cacheRef.current = {
      observationId,
      mainUrl: liveMainUrl ?? null,
      segmentUrls: {},
    };
  }
  if (
    observationId &&
    cacheRef.current.observationId === observationId &&
    !cacheRef.current.mainUrl &&
    liveMainUrl
  ) {
    cacheRef.current.mainUrl = liveMainUrl;
  }
  liveSegments.forEach((seg) => {
    if (
      cacheRef.current.segmentUrls[seg.id] == null &&
      seg.recordingDownloadUrl
    ) {
      cacheRef.current.segmentUrls[seg.id] = seg.recordingDownloadUrl;
    }
  });

  return {
    mainUrl: cacheRef.current.mainUrl,
    getSegmentUrl: (segmentId: string) =>
      cacheRef.current.segmentUrls[segmentId] ?? null,
  };
}

export default function ObservationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const videoRef = useRef<HTMLVideoElement>(null);

  const idList = useMemo(() => (id ? [id] : []), [id]);
  const { displayCodes } = useObservationDisplayCodes(idList);
  const displayCode = useMemo(
    () => (id ? displayCodes[id] ?? null : null),
    [displayCodes, id]
  );

  const { data, isLoading, error, refetch } = useQuery<ObservationDetail>({
    queryKey: ["observation", id],
    queryFn: async () => {
      const res = await axios.get<{
        success: boolean;
        data: ObservationDetail;
      }>(`${API_BASE_URL}/api/observations/${id}`, {
        headers: getAuthHeaders(),
      });
      return res.data.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) => {
      const d = query.state.data;
      return d && isProcessingInProgress(d.processingStatus) ? 5000 : false;
    },
  });

  const { mainUrl: stableMainUrl, getSegmentUrl } = useStableRecordingUrls(
    data?.id,
    data?.recordingDownloadUrl,
    data?.childSegments ?? []
  );

  const handleBackToList = useCallback(() => {
    navigate("/admin/observations");
  }, [navigate]);

  const handleSeekVideo = useCallback((timeSec: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = timeSec;
      void videoRef.current.play();
    }
  }, []);

  const handleViewSessionRecording = useCallback(() => {
    if (!data?.sessionRecordingId || !data.reporterId) return;
    const dateSource = data.recordingStartedAt ?? data.createdAt;
    const date = dayjs.utc(dateSource).format("YYYY-MM-DD");
    const params = new URLSearchParams({
      sessionId: data.sessionRecordingId,
      userId: data.reporterId,
      date,
    });
    navigate(`/admin/session-recordings?${params.toString()}`);
  }, [data, navigate]);

  const handleAnalyze = useCallback(
    async (observationId: string) => {
      try {
        await triggerAnalysis(observationId);
        await refetch();
        notifications.show({
          title: "Analysis started",
          message: "AI analysis is running in the background.",
          color: "blue",
        });
      } catch (err) {
        notifications.show({
          title: "Failed to start analysis",
          message: err instanceof Error ? err.message : "Unknown error",
          color: "red",
        });
      }
    },
    [refetch]
  );

  // Single mutation using the combined PATCH /api/observations/:id endpoint
  const updateMutation = useMutation({
    mutationFn: (payload: {
      status?: ObservationStatus;
      severity?: ObservationSeverity | null;
      category?: AnalysisCategory | null;
      title?: string;
      summary?: string;
    }) => observationsService.update(String(id), payload),
    onSuccess: () => {
      // Both keys: the list caches status and severity too, so leaving it alone
      // sends you back to a row that still shows the value you just changed.
      queryClient.invalidateQueries({ queryKey: ["observation", id] });
      queryClient.invalidateQueries({ queryKey: ["observations"] });
      notifications.show({
        title: "Updated",
        message: "Observation updated successfully.",
        color: "green",
      });
    },
    onError: (err) => {
      notifications.show({
        title: "Couldn't update observation",
        message:
          err instanceof Error ? err.message : "Failed to update observation",
        color: "red",
      });
    },
  });

  const handleStatusChange = useCallback(
    (val: string | null) => {
      if (isObservationStatus(val) && val !== data?.status) {
        updateMutation.mutate({ status: val });
      }
    },
    [data?.status, updateMutation]
  );

  const handleSeverityChange = useCallback(
    (val: string | null) => {
      if (val !== (data?.severity ?? null)) {
        updateMutation.mutate({
          severity: isObservationSeverity(val) ? val : null,
        });
      }
    },
    [data?.severity, updateMutation]
  );

  const currentCategory = useMemo(
    () => readCategoryFromMetadata(data?.analysisMetadata ?? null),
    [data?.analysisMetadata]
  );

  const handleCategoryChange = useCallback(
    (val: string | null) => {
      if (val !== currentCategory) {
        updateMutation.mutate({
          category: val ? (val as AnalysisCategory) : null,
        });
      }
    },
    [currentCategory, updateMutation]
  );

  const handleUpdateTitle = useCallback(
    async (_observationId: string, title: string) => {
      await updateMutation.mutateAsync({ title });
    },
    [updateMutation]
  );

  const handleUpdateSummary = useCallback(
    async (_observationId: string, summary: string) => {
      await updateMutation.mutateAsync({ summary });
    },
    [updateMutation]
  );

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader />
      </Stack>
    );
  }

  if (error || !data) {
    return (
      <Stack p="md">
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          title="Couldn't load observation"
          role="alert"
        >
          {error instanceof Error
            ? error.message
            : "This observation could not be loaded."}
        </Alert>
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={handleBackToList}
        >
          Back to Observations
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" p="md">
      {/* Header */}
      <Group justify="space-between" align="center">
        <Group>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={handleBackToList}
          >
            Back
          </Button>
          <Title order={2}>
            {displayCode
              ? `${displayCode} — Observation Detail`
              : "Observation Detail"}
          </Title>
          <Badge
            color={observationStatusColor(data.status)}
            variant="light"
            size="lg"
          >
            {observationStatusLabel(data.status)}
          </Badge>
          {data.severity && (
            <Badge
              color={observationSeverityColor(data.severity)}
              variant="filled"
              size="lg"
            >
              {data.severity}
            </Badge>
          )}
          {currentCategory && (
            <ObservationCategoryBadge category={currentCategory} />
          )}
        </Group>

        <Group gap="sm">
          <Select
            label="Status"
            value={data.status}
            onChange={handleStatusChange}
            data={OBSERVATION_STATUS_OPTIONS}
            w={180}
            disabled={updateMutation.isPending}
          />
          <Select
            label="Severity"
            placeholder="Not set"
            clearable
            value={data.severity ?? null}
            onChange={handleSeverityChange}
            data={OBSERVATION_SEVERITY_OPTIONS}
            w={160}
            disabled={updateMutation.isPending}
          />
          <Select
            label="Category"
            placeholder="Not set"
            clearable
            value={currentCategory}
            onChange={handleCategoryChange}
            data={CATEGORY_OPTIONS}
            w={180}
            disabled={updateMutation.isPending}
          />
        </Group>
      </Group>

      <Grid gutter="lg" align="flex-start">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="lg">
            {stableMainUrl ? (
              <Paper withBorder p="md">
                <Title order={4} mb="sm">
                  Screen Recording
                </Title>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                  ref={videoRef}
                  controls
                  src={stableMainUrl}
                  style={{ width: "100%", maxHeight: 600, borderRadius: 8 }}
                />
                <Button
                  component="a"
                  href={stableMainUrl}
                  download
                  variant="light"
                  size="sm"
                  mt="sm"
                  leftSection={<IconDownload size={14} />}
                >
                  Download recording
                </Button>
              </Paper>
            ) : (
              data.childSegments.length === 0 && (
                <Paper withBorder p="md">
                  <Text c="dimmed" ta="center" py="xl">
                    No screen recordings available for this observation.
                  </Text>
                </Paper>
              )
            )}

            <ObservationCommentsThread
              observationId={data.id}
              displayCode={displayCode}
            />
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="lg">
            <ObservationMetadataCard
              reporterName={data.reporterName}
              reporterEmail={data.reporterEmail}
              createdAt={data.createdAt}
              updatedAt={data.updatedAt}
              canViewSessionRecording={Boolean(
                data.sessionRecordingId && data.reporterId
              )}
              onViewSessionRecording={handleViewSessionRecording}
            />

            <div className={styles.analysisPanel}>
              <ObservationAnalysisPanel
                observation={data as unknown as PackageObservationDetail}
                onSeek={handleSeekVideo}
                onRetry={handleAnalyze}
                onAnalyze={handleAnalyze}
                onUpdateTitle={handleUpdateTitle}
                onUpdateSummary={handleUpdateSummary}
              />
            </div>

            {data.childSegments.length > 0 && (
              <Paper withBorder p="md">
                <Title order={4} mb="sm">
                  Additional Segments ({data.childSegments.length})
                </Title>
                <Stack gap="md">
                  {data.childSegments.map((seg, idx) => (
                    <div key={seg.id}>
                      <Text size="sm" fw={500} mb="xs">
                        Segment {idx + 2}
                        {seg.recordingStartedAt &&
                          ` — ${dayjs(seg.recordingStartedAt).format(
                            "h:mm:ss A"
                          )}`}
                      </Text>
                      {getSegmentUrl(seg.id) ? (
                        // eslint-disable-next-line jsx-a11y/media-has-caption
                        <video
                          controls
                          src={getSegmentUrl(seg.id) ?? undefined}
                          style={{
                            width: "100%",
                            maxHeight: 300,
                            borderRadius: 8,
                          }}
                        />
                      ) : (
                        <Text size="sm" c="dimmed">
                          No recording available for this segment
                        </Text>
                      )}
                      {idx < data.childSegments.length - 1 && (
                        <Divider mt="md" />
                      )}
                    </div>
                  ))}
                </Stack>
              </Paper>
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
